import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Button, Card, Badge } from '../../components/primitives/index.js'
import { customerApi } from '../../lib/customerApi.js'
import { fmtMoney, fmtDate } from '../../utils/invoiceUtils.js'
import './AccountReferrals.css'

function CreditCardIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="1" y="4" width="22" height="16" rx="2" />
      <path d="M1 10h22" />
    </svg>
  )
}

function CashIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="1" y="5" width="22" height="14" rx="2" />
      <circle cx="12" cy="12" r="3.2" />
      <path d="M5 5v.01M19 19v-.01" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

const PAYOUT_STATUS_TONE = {
  pending: 'warning',
  approved: 'success',
  denied: 'danger',
}

const EVENT_STATUS_TONE = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
}

function PayoutRow({ p }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="account-referrals__payout-row">
      <button type="button" className="account-referrals__payout-toggle" onClick={() => setOpen((o) => !o)}>
        <span>{fmtMoney(p.amount)}</span>
        <span className="account-referrals__payout-toggle-right">
          <Badge tone={PAYOUT_STATUS_TONE[p.status] ?? 'neutral'}>{p.status}</Badge>
          <span className="account-referrals__event-date">{open ? '▾' : '▸'}</span>
        </span>
      </button>
      {open && (
        <div className="account-referrals__payout-detail">
          <p><strong>Requested:</strong> {fmtDate(p.requestedAt)}</p>
          <p><strong>Sent to:</strong> {p.destination === 'store_credit' ? 'Store credit' : `${p.method} — ${p.methodDetail}`}</p>
          {p.status !== 'pending' && p.decidedAt && <p><strong>Decided:</strong> {fmtDate(p.decidedAt)}</p>}
          {p.status === 'denied' && p.denialReason && <p><strong>Reason:</strong> {p.denialReason}</p>}
        </div>
      )}
    </div>
  )
}

function PayoutRequestForm({ data, onDone }) {
  const [destination, setDestination] = useState('store_credit')
  const [method, setMethod] = useState(data.payoutMethods?.[0] ?? '')
  const [methodDetail, setMethodDetail] = useState('')
  const [amount, setAmount] = useState(String(data.cashBalance))
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function submit(e) {
    e.preventDefault()
    setError('')
    const amt = Number(amount)
    if (!amt || amt <= 0) return setError('Enter an amount to request.')
    if (amt > data.cashBalance) return setError('That amount is more than your current referral cash balance.')
    if (amt < data.minPayoutAmount) return setError(`Minimum payout is ${fmtMoney(data.minPayoutAmount)}.`)
    if (destination === 'cash' && !methodDetail.trim()) return setError(`Enter your ${method} details (email, handle, etc).`)
    setSubmitting(true)
    try {
      await customerApi.requestReferralPayout({
        destination,
        amount: amt,
        method: destination === 'cash' ? method : undefined,
        methodDetail: destination === 'cash' ? methodDetail.trim() : undefined,
      })
      onDone()
    } catch (err) {
      setError(err.message ?? 'Could not submit the request.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form className="account-referrals__payout-form" onSubmit={submit}>
      <div className="account-referrals__field">
        <span className="account-referrals__label">Payout amount</span>
        <div className="account-referrals__amount-input">
          <span className="account-referrals__amount-prefix">$</span>
          <input
            className="account-referrals__input account-referrals__input--amount"
            type="number" min="0" step="0.01" max={data.cashBalance}
            value={amount} onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <p className="account-referrals__field-hint">Available: {fmtMoney(data.cashBalance)}</p>
      </div>

      <div className="account-referrals__field">
        <span className="account-referrals__label">Payout method</span>
        <div className="account-referrals__destination-toggle" role="radiogroup" aria-label="Payout destination">
          <button
            type="button" role="radio" aria-checked={destination === 'store_credit'}
            className={`account-referrals__destination-option${destination === 'store_credit' ? ' is-active' : ''}`}
            onClick={() => setDestination('store_credit')}
          >
            <span className="account-referrals__destination-icon"><CreditCardIcon /></span>
            <span className="account-referrals__destination-copy">
              <span className="account-referrals__destination-name">Store Credit</span>
              <span className="account-referrals__destination-desc">Added to your account instantly</span>
            </span>
            <span className="account-referrals__destination-radio">{destination === 'store_credit' && <CheckIcon />}</span>
          </button>
          <button
            type="button" role="radio" aria-checked={destination === 'cash'}
            className={`account-referrals__destination-option${destination === 'cash' ? ' is-active' : ''}`}
            onClick={() => setDestination('cash')}
          >
            <span className="account-referrals__destination-icon"><CashIcon /></span>
            <span className="account-referrals__destination-copy">
              <span className="account-referrals__destination-name">Cash Payout</span>
              <span className="account-referrals__destination-desc">Sent to your chosen method</span>
            </span>
            <span className="account-referrals__destination-radio">{destination === 'cash' && <CheckIcon />}</span>
          </button>
        </div>
      </div>

      {destination === 'cash' && (
        <div className="account-referrals__field">
          <span className="account-referrals__label">Payout details</span>
          <select className="account-referrals__select" value={method} onChange={(e) => setMethod(e.target.value)}>
            {(data.payoutMethods ?? []).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <input
            className="account-referrals__input"
            value={methodDetail}
            onChange={(e) => setMethodDetail(e.target.value)}
            placeholder={`Your ${method} email or handle`}
          />
        </div>
      )}

      {error && <p className="account-referrals__error">{error}</p>}

      <Button type="submit" variant="primary" size="sm" loading={submitting}>
        Submit request
      </Button>
    </form>
  )
}

export default function AccountReferrals() {
  const [data, setData] = useState(null)
  const [payouts, setPayouts] = useState(null)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const [showForm, setShowForm] = useState(false)

  function load() {
    Promise.all([customerApi.getReferrals(), customerApi.getReferralPayouts()])
      .then(([d, p]) => { setData(d); setPayouts(p) })
      .catch((e) => setError(e.message))
  }
  useEffect(() => { load() }, [])

  if (error) return <Card>{error}</Card>
  if (!data || !payouts) return <Card>Loading referrals&hellip;</Card>
  if (!data.enabled) return <Card>The referral program isn&apos;t active right now — check back soon.</Card>

  const friendLabel = data.friendReward?.type === 'percent'
    ? `${data.friendReward.value}% off`
    : `${fmtMoney(data.friendReward?.value ?? 0)} off`
  const referrerLabel = data.referrerReward?.type === 'percent'
    ? `${data.referrerReward.value}% of their order`
    : data.referrerReward?.type === 'tiered'
      ? `${data.referrerReward.belowPercent}% of their order (${data.referrerReward.abovePercent}% if it's ${fmtMoney(data.referrerReward.threshold)} or more)`
      : fmtMoney(data.referrerReward?.value ?? 0)

  const hasPending = payouts.some((p) => p.status === 'pending')
  const canRequest = data.cashBalance >= data.minPayoutAmount && !hasPending

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(data.url ?? '')
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch { /* clipboard unavailable */ }
  }

  return (
    <div className="account-referrals">
      <Card className="account-referrals__code-card">
        <div className="account-referrals__code-main">
          <span className="account-referrals__label">Your referral link</span>
          <code className="account-referrals__code">{data.url ?? '—'}</code>
          <p className="account-referrals__pitch">
            Friends who join through your link get <strong>{friendLabel}</strong> their first order —
            and you earn <strong>{referrerLabel}</strong> once it&apos;s confirmed.
          </p>
        </div>
        <Button variant="primary" onClick={copyLink}>
          {copied ? 'Copied!' : 'Copy link'}
        </Button>
      </Card>

      <div className="account-referrals__stats">
        <Card>
          <span className="account-referrals__label">Friends joined</span>
          <span className="account-referrals__value">{data.stats?.invited ?? 0}</span>
        </Card>
        <Card>
          <span className="account-referrals__label">Qualified orders</span>
          <span className="account-referrals__value">{data.stats?.qualified ?? 0}</span>
        </Card>
        <Card>
          <span className="account-referrals__label">Cash earned</span>
          <span className="account-referrals__value">{fmtMoney(data.stats?.cashEarned ?? 0)}</span>
        </Card>
      </div>

      <Card className="account-referrals__balance-card">
        <div>
          <span className="account-referrals__label">Referral cash balance</span>
          <span className="account-referrals__balance-value">{fmtMoney(data.cashBalance)}</span>
        </div>
        {!showForm && (
          <Button variant="secondary" disabled={!canRequest} onClick={() => setShowForm(true)}>
            {hasPending ? 'Request pending' : 'Request payout'}
          </Button>
        )}
      </Card>
      {!canRequest && !hasPending && (
        <p className="account-referrals__hint">You need at least {fmtMoney(data.minPayoutAmount)} to request a payout.</p>
      )}
      {hasPending && (
        <p className="account-referrals__hint">You have a payout request awaiting approval — check its status below.</p>
      )}

      {showForm && (
        <Card>
          <PayoutRequestForm data={data} onDone={() => { setShowForm(false); load() }} />
        </Card>
      )}

      {payouts.length > 0 && (
        <>
          <h4 className="account-referrals__section-title">Payout requests</h4>
          <Card className="account-referrals__payouts-card">
            {payouts.map((p) => <PayoutRow key={p.id} p={p} />)}
          </Card>
        </>
      )}

      <h4 className="account-referrals__section-title">Referral history</h4>
      <div className="account-referrals__events">
        {(data.events ?? []).map((event, idx) => (
          <motion.div key={event.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
            <Card className="account-referrals__event">
              <span>{event.referred}</span>
              <Badge tone={EVENT_STATUS_TONE[event.status] ?? 'neutral'}>{event.status === 'pending' ? 'under review' : event.status}</Badge>
              {event.status === 'approved' && <span>+{fmtMoney(event.amount)}</span>}
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  )
}
